import _ from "lodash";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createStructuredSelector } from "reselect";
import { connect, useDispatch, useSelector } from "react-redux";
import { Typography, Select, Space, Checkbox, Tooltip, Spin } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { MultiSelectDrugSearch } from "@tailormed/common-client/component/MultiSelectDrugSearch";
import { withTranslation } from "react-i18next";
import { patientTableColumns } from "../constant/patientTableColumns";
import { Statuses } from "../constant/status";
import {
  selectPatients,
  selectFilters,
  selectAssigneesAndUserGroups,
  selectSorting,
  selectMultiples
} from "../store/selector";
import ACTIONS from "../store/action";
import ClearIcon from "../assets/svg/ClearIcon";
import {
  defaultFilters,
  ALL,
  BILL_VOIDED,
  LAST_MONTH,
  LAST_WEEK,
  ORDER_VIEW,
  THIS_WEEK,
  THIS_MONTH,
  FILTER_PATIENT_ASSIGNEE,
  ORDER_TYPES,
  defaultMultiples
} from "../constant/filters";
import { ReactComponent as FilterByIcon } from "../assets/svg/filter-by-icon.svg";
import { DateRangeFilter } from "./filter/DateRangeFilter";
import {
  ApiNames,
  searchRxnormDrugsAPI,
  searchPhysiciansAPI,
  searchFacilitiesAPI,
  searchAPSponsorsAPI,
  fetchProvidersByIdsAPI
} from "../api/api";
import { MultiSelect, SingleSelect } from "./customComponent/CustomSelect";
import { formatAssignee } from "../utils/formaters";
import { GRAY, NEW_BLUE, RED_GRAD, SEC_FONT_GREY, STEEL_BLUE, WHITE } from "../constant/colors";
import { DOSE_FORM_CATEGORY_TYPES } from "../constant/drugs";
import { defaultSort, filterSort } from "../utils/sort";
import styled from "styled-components";

const { Text } = Typography;
const { Option } = Select;

const StyledDiv = styled.div`
  flex: 0 0 282px;
  width: ${(props) => (props.filtersVisible ? "310px" : "0px")};
  max-width: ${(props) => (props.filtersVisible ? "310px" : "0px")};
  transition: max-width 0.3s ease;
`;

const StyledSpace = styled(Space)`
  ${(props) =>
    props.filtersVisible &&
    `height: ${
      props.isCalendar && !props.isSavedListView
        ? "calc(100vh - 250px)"
        : props.isSavedListView
        ? "calc(100vh - 285px)"
        : "calc(100vh - 245px)"
    };
    ::-webkit-scrollbar {
      width: 15px;
      -webkit-appearance: none;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 8px;
      border: 4px solid white;
      background-color: rgba(0, 0, 0, 0.2);
    }
  `}
  margin-left: ${(props) => (props.isCalendar ? "50px" : "0px")};
  background-color: ${(props) => (props.filtersVisible ? "#F9F9F9" : "white")};
  padding: ${(props) => (props.filtersVisible ? "17px 0px" : "0")};
  max-width: ${(props) => (props.filtersVisible ? "282px" : "none")};
  min-width: 110px;
  width: ${(props) => (props.filtersVisible ? "100%" : "0px")};
  position: relative;
  overflow: auto;
  top: ${(props) => {
    return props.filtersVisible ? undefined : "8px";
  }};
  z-index: ${(props) => (!props.filtersVisible ? "1" : "auto")};
  margin-bottom: ${(props) => {
    return props.filtersVisible ? "15px" : undefined;
  }};
`;

const MIN_SEARCH_CHARS = 3;
const DEBOUNCE_MS = 400;

const OrderFiltersComponent = ({
  t,
  filters,
  assignees,
  filtersVisible,
  setFiltersVisible,
  isSavedListView,
  isCalendar = false
}) => {
  const dispatch = useDispatch();
  const [selectedFilters, setSelectedFilters] = useState(filters);
  const [isDrugNameSelectOpen, setIsDrugNameSelectOpen] = useState(false);
  const [isDrugNameSelectHover, setIsDrugNameSelectHover] = useState(false);
  const [physicianSearchText, setPhysicianSearchText] = useState("");
  const [physicianSearchResults, setPhysicianSearchResults] = useState([]);
  const [isPhysicianFetching, setIsPhysicianFetching] = useState(false);

  // Facility search state
  const [facilitySearchText, setFacilitySearchText] = useState("");
  const [facilitySearchResults, setFacilitySearchResults] = useState([]);
  const [isFacilityFetching, setIsFacilityFetching] = useState(false);

  const debouncedPhysicianSearch = useRef(
    _.debounce(async (query) => {
      if (query.length < MIN_SEARCH_CHARS) {
        setPhysicianSearchResults([]);
        setIsPhysicianFetching(false);
        return;
      }
      try {
        const response = await searchPhysiciansAPI(query, { limit: 100 });
        setPhysicianSearchResults(response?.data?.providers || []);
      } catch (error) {
        setPhysicianSearchResults([]);
      } finally {
        setIsPhysicianFetching(false);
      }
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => debouncedPhysicianSearch.cancel();
  }, [debouncedPhysicianSearch]);

  const handlePhysicianSearch = useCallback(
    (value) => {
      setPhysicianSearchText(value);
      if (value.length < MIN_SEARCH_CHARS) {
        setPhysicianSearchResults([]);
        setIsPhysicianFetching(false);
        return;
      }
      setIsPhysicianFetching(true);
      debouncedPhysicianSearch(value);
    },
    [debouncedPhysicianSearch]
  );

  // Facility search (server-side, like physicians)
  const debouncedFacilitySearch = useRef(
    _.debounce(async (query) => {
      if (query.length < MIN_SEARCH_CHARS) {
        setFacilitySearchResults([]);
        setIsFacilityFetching(false);
        return;
      }
      try {
        const response = await searchFacilitiesAPI(query);
        const facilities = response?.data?.providers || [];
        setFacilitySearchResults(facilities);

        // Store in Redux and ref
        facilities.forEach((f) => {
          allKnownFacilities.current.set(String(f.id), f);
          facilityIdToNameMap.current.set(String(f.id), f.name);
        });
      } catch (error) {
        setFacilitySearchResults([]);
      } finally {
        setIsFacilityFetching(false);
      }
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => debouncedFacilitySearch.cancel();
  }, [debouncedFacilitySearch]);

  const handleFacilitySearch = useCallback(
    (value) => {
      setFacilitySearchText(value);
      if (value.length < MIN_SEARCH_CHARS) {
        setFacilitySearchResults([]);
        setIsFacilityFetching(false);
        return;
      }
      setIsFacilityFetching(true);
      debouncedFacilitySearch(value);
    },
    [debouncedFacilitySearch]
  );

  const getFacilityNotFoundContent = () => {
    if (facilitySearchText.length < MIN_SEARCH_CHARS) return t("type_to_search");
    if (isFacilityFetching) return <Spin size="small" indicator={<LoadingOutlined spin />} />;
    return undefined;
  };

  // All known facilities (from search + saved filter resolution)
  const allKnownFacilities = useRef(new Map());
  // Simple lookup: facility ID → name (for display purposes)
  const facilityIdToNameMap = useRef(new Map());

  // Sponsor search state (search-on-type, like physicians/facilities)
  const [sponsorSearchText, setSponsorSearchText] = useState("");
  const [sponsorSearchResults, setSponsorSearchResults] = useState([]);
  const [isSponsorFetching, setIsSponsorFetching] = useState(false);

  const debouncedSponsorSearch = useRef(
    _.debounce(async (query) => {
      if (query.length < MIN_SEARCH_CHARS) {
        setSponsorSearchResults([]);
        setIsSponsorFetching(false);
        return;
      }
      try {
        const response = await searchAPSponsorsAPI(query);
        const sponsors = response?.data?.APSponsors || [];
        setSponsorSearchResults(sponsors);
      } catch (error) {
        setSponsorSearchResults([]);
      } finally {
        setIsSponsorFetching(false);
      }
    }, DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => debouncedSponsorSearch.cancel();
  }, [debouncedSponsorSearch]);

  const handleSponsorSearch = useCallback(
    (value) => {
      setSponsorSearchText(value);
      if (value.length < MIN_SEARCH_CHARS) {
        setSponsorSearchResults([]);
        setIsSponsorFetching(false);
        return;
      }
      setIsSponsorFetching(true);
      debouncedSponsorSearch(value);
    },
    [debouncedSponsorSearch]
  );

  const getSponsorNotFoundContent = () => {
    if (sponsorSearchText.length < MIN_SEARCH_CHARS) return t("type_to_search");
    if (isSponsorFetching) return <Spin size="small" indicator={<LoadingOutlined spin />} />;
    return undefined;
  };

  // On mount, resolve saved facility filter IDs
  const savedFacilityResolved = useRef(false);
  const [resolvedFacilityNames, setResolvedFacilityNames] = useState([]);

  useEffect(() => {
    const savedIds = filters.facilitiesFilter || [];
    const needsFetch = savedIds.length > 0 && savedIds.some((id) => !facilityIdToNameMap.current.has(String(id)));

    if (needsFetch && !savedFacilityResolved.current) {
      savedFacilityResolved.current = true;
      fetchProvidersByIdsAPI(savedIds)
        .then((res) => {
          const allProviders = res?.data?.providers || res?.data || [];
          const providers = allProviders.filter((f) => savedIds.includes(Number(f.id)));
          providers.forEach((f) => {
            allKnownFacilities.current.set(String(f.id), f);
            facilityIdToNameMap.current.set(String(f.id), f.name);
          });

          setResolvedFacilityNames(providers.map((f) => f.name));
        })
        .catch(() => {})
        .finally(() => {
          savedFacilityResolved.current = false;
        });
    }
  }, [filters.facilitiesFilter]);

  const facilityTooltipContent = React.useMemo(() => {
    return (filters.facilitiesFilter || []).map((id) => {
      const name = facilityIdToNameMap.current.get(String(id)) || "";
      return { text: name, value: id };
    });
  }, [filters.facilitiesFilter, resolvedFacilityNames]);

  // Combine search results + saved known facilities for options
  const facilityOptions = React.useMemo(() => {
    const map = new Map();
    facilitySearchResults.forEach((f) => {
      map.set(String(f.id), f);
    });
    const savedIds = filters.facilitiesFilter || [];
    savedIds.forEach((id) => {
      const name = facilityIdToNameMap.current.get(String(id));
      if (name && !map.has(String(id))) {
        map.set(String(id), { id: Number(id), name });
      }
    });
    allKnownFacilities.current.forEach((f, id) => {
      if (!map.has(id)) {
        map.set(id, f);
      }
    });
    return Array.from(map.values());
  }, [facilitySearchResults, resolvedFacilityNames, filters.facilitiesFilter]);

  // On mount, pre-load saved sponsors so they appear in dropdown
  useEffect(() => {
    const savedSponsors = filters.manufacturersFilter || [];
    if (savedSponsors.length > 0) {
      // Add saved sponsors to search results immediately
      setSponsorSearchResults((prev) => {
        const combined = [...new Set([...savedSponsors, ...prev])];
        return combined;
      });
    }
  }, []); // Run once on mount

  // Sponsor tooltip content (like physicians/facilities)
  const sponsorTooltipContent = React.useMemo(() => {
    return (filters.manufacturersFilter || []).map((sponsor) => {
      return { text: sponsor, value: sponsor };
    });
  }, [filters.manufacturersFilter]);

  const setFiltersAct = (filters, apiName, page, sorting) =>
    dispatch(ACTIONS.setFiltersAction(filters, apiName, page, sorting));
  const setCurrentPageAct = (page) => dispatch(ACTIONS.setCurrentPage(page));
  const setSortingAct = (sorting) => dispatch(ACTIONS.setSorting(sorting));
  const setFiltersObjectAct = (filters) => dispatch(ACTIONS.setFilters(filters));
  const setMultiplesFilter = (multiples) => dispatch(ACTIONS.setMultiplesFilter(multiples));
  const sorting = useSelector(selectSorting);
  const multiples = useSelector(selectMultiples);

  useEffect(() => {
    setSelectedFilters(filters);
  }, [filters]);

  // Group search results by NPI for deduplication
  const uniquePhysicians = React.useMemo(() => {
    const npiMap = new Map();

    physicianSearchResults.forEach((physician) => {
      const npi = physician.npi || `NO_NPI_${physician.id}`;
      if (!npiMap.has(npi)) {
        npiMap.set(npi, { npi, name: physician.name, ids: [] });
      }
      npiMap.get(npi).ids.push(String(physician.id));
    });

    return Array.from(npiMap.values());
  }, [physicianSearchResults]);

  // Build a combined lookup of NPI physicians (from search results + saved filter resolution)
  const allKnownPhysicians = React.useRef(new Map());
  // Reverse lookup: provider ID → NPI
  const idToNpiMap = React.useRef(new Map());
  // Simple lookup: provider ID → name (for display purposes)
  const idToNameMap = React.useRef(new Map());

  useEffect(() => {
    uniquePhysicians.forEach((p) => {
      if (!allKnownPhysicians.current.has(p.npi)) {
        allKnownPhysicians.current.set(p.npi, p);
      }
      p.ids.forEach((id) => {
        idToNpiMap.current.set(String(id), p.npi);
        idToNameMap.current.set(String(id), p.name); // Store name directly
      });
    });
  }, [uniquePhysicians]);

  // On mount, resolve saved physician filter IDs to NPI by fetching those specific providers
  const savedFilterResolved = useRef(false);
  const [resolvedNpis, setResolvedNpis] = useState([]);

  useEffect(() => {
    const savedIds = filters.physiciansFilter || [];
    const needsFetch = savedIds.length > 0 && savedIds.some((id) => !idToNpiMap.current.has(String(id)));

    if (needsFetch && !savedFilterResolved.current) {
      savedFilterResolved.current = true;
      fetchProvidersByIdsAPI(savedIds)
        .then((res) => {
          const allProviders = res?.data?.providers || res?.data || [];
          const savedIdsSet = new Set(savedIds.map((id) => String(id)));
          const providers = allProviders.filter((p) => savedIdsSet.has(String(p.id)));

          providers.forEach((p) => {
            const npi = p.npi || `NO_NPI_${p.id}`;
            idToNpiMap.current.set(String(p.id), npi);
            idToNameMap.current.set(String(p.id), p.name);
            if (!allKnownPhysicians.current.has(npi)) {
              const physicianData = { npi, name: p.name, ids: [String(p.id)] };
              allKnownPhysicians.current.set(npi, physicianData);
            } else {
              const existing = allKnownPhysicians.current.get(npi);
              if (!existing.ids.includes(String(p.id))) {
                existing.ids.push(String(p.id));
              }
            }
          });

          const npis = new Set();
          savedIds.forEach((id) => {
            const npi = idToNpiMap.current.get(String(id)) || `NO_NPI_${id}`;
            npis.add(npi);
          });
          setResolvedNpis(Array.from(npis));
        })
        .catch(() => {})
        .finally(() => {
          savedFilterResolved.current = false;
        });
    }
  }, [filters.physiciansFilter]);

  // Combine search results with saved physicians for complete options list
  const allPhysicianOptions = React.useMemo(() => {
    const optionsMap = new Map();

    uniquePhysicians.forEach((p) => {
      optionsMap.set(p.npi, p);
    });

    const savedIds = filters.physiciansFilter || [];
    savedIds.forEach((id) => {
      const npi = idToNpiMap.current.get(String(id));
      const name = idToNameMap.current.get(String(id));
      if (npi && name && !optionsMap.has(npi)) {
        const idsForNpi = Array.from(idToNpiMap.current.entries())
          .filter(([, n]) => n === npi)
          .map(([id]) => id);
        optionsMap.set(npi, { npi, name, ids: idsForNpi });
      }
    });

    return Array.from(optionsMap.values());
  }, [uniquePhysicians, filters.physiciansFilter, resolvedNpis]);

  const selectedNpis = React.useMemo(() => {
    const npis = new Set();
    (filters.physiciansFilter || []).forEach((id) => {
      const npi = idToNpiMap.current.get(String(id)) || `NO_NPI_${id}`;
      npis.add(npi);
    });
    return Array.from(npis);
  }, [filters.physiciansFilter, resolvedNpis]);

  const physicianTooltipContent = React.useMemo(() => {
    return (filters.physiciansFilter || []).map((id) => {
      const name = idToNameMap.current.get(String(id)) || "";
      return { text: name, value: id };
    });
  }, [filters.physiciansFilter, resolvedNpis]);

  const handlePhysicianChange = (selectedNpis, options) => {
    const allIds = selectedNpis.flatMap((npi) => {
      const physician = allKnownPhysicians.current.get(npi);
      return physician ? physician.ids : [];
    });

    handleInputMultiSelect(allIds, "physiciansFilter", options);
  };

  const getPhysicianNotFoundContent = () => {
    if (physicianSearchText.length < MIN_SEARCH_CHARS) return t("type_to_search");
    if (isPhysicianFetching) return <Spin size="small" indicator={<LoadingOutlined spin />} />;
    return undefined;
  };

  const onClickReset = (event, isOpen) => {
    event.stopPropagation();
    setCurrentPageAct(1);
    setFiltersObjectAct({ ...defaultFilters, isOpen });
    setFiltersAct({ ...defaultFilters, isOpen, forceCallApi: true });
    setSelectedFilters({ ...defaultFilters, isOpen });
    setSortingAct(defaultSort);
    setMultiplesFilter(defaultMultiples);
  };

  const handleShowInactiveCheckbox = (e) => {
    setSelectedFilters({
      ...selectedFilters,
      showInactiveFilter: e.target.checked ? "true" : "false"
    });
    setFiltersObjectAct({
      ...selectedFilters,
      showInactiveFilter: e.target.checked ? "true" : "false"
    });
    setCurrentPageAct(1);
    setFiltersAct(
      { ...selectedFilters, showInactiveFilter: e.target.checked ? "true" : "false", forceCallApi: true },
      ApiNames.Search,
      1,
      sorting
    );
    window.scroll(0, 0);
  };

  const handleInputSingleSelect = (value, filterName) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterName]: value
    });
    setFiltersObjectAct({
      ...selectedFilters,
      [filterName]: value
    });
    setCurrentPageAct(1);
    setFiltersAct(
      {
        ...selectedFilters,
        [filterName]: value,
        forceCallApi: true
      },
      ApiNames.Search,
      1,
      sorting
    );
  };

  const handleInputDate = (fromDate, toDate, filterName) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterName]: { fromDate, toDate }
    });
    setFiltersObjectAct({ ...selectedFilters, [filterName]: { fromDate, toDate } });
    setCurrentPageAct(1);
    setFiltersAct(
      {
        ...selectedFilters,
        [filterName]: { fromDate, toDate },
        forceCallApi: true
      },
      ApiNames.Search,
      1,
      sorting
    );
  };

  const handleInputMultiSelect = (values, filterName, options) => {
    const filterValues = [];
    options.map((option, index) => {
      filterValues[index] = { value: option.value, text: option.children };
    });
    setSelectedFilters({ ...selectedFilters, [filterName]: values });
    setFiltersObjectAct({ ...selectedFilters, [filterName]: values, forceCallApi: true });
    setCurrentPageAct(1);
    setFiltersAct(
      {
        ...filters,
        [filterName]: values,
        forceCallApi: true
      },
      ApiNames.Search,
      1,
      sorting
    );
    setMultiplesFilter({
      ...multiples,
      [filterName]: filterValues
    });
  };

  const handleCheckUnassigned = (value) => {
    setFiltersObjectAct({ ...selectedFilters, isUnassigned: value, assigneesFilter: [], forceCallApi: true });
    setFiltersAct({
      ...filters,
      isUnassigned: value,
      assigneesFilter: []
    });

    setMultiplesFilter({
      ...multiples,
      assigneesFilter: []
    });
  };

  const handleSelectedDrugsChange = (selectedDrugs) => {
    setSelectedFilters({ ...selectedFilters, drugsFilter: selectedDrugs });
    setFiltersObjectAct({ ...selectedFilters, drugsFilter: selectedDrugs, forceCallApi: true });
    setCurrentPageAct(1);
    setFiltersAct({
      ...filters,
      drugsFilter: selectedDrugs,
      forceCallApi: true
    });

    setMultiplesFilter({
      ...multiples,
      drugsFilter: selectedDrugs
    });
  };

  const drugNameTextToDisplay =
    (filters.drugsFilter &&
      filters.drugsFilter.length > 0 &&
      filters.drugsFilter.map(({ tmDisplayName }) => tmDisplayName).join(" | ")) ||
    "";

  const selectedFiltersNum = () => {
    const {
      statusesFilter,
      physiciansFilter,
      facilitiesFilter,
      manufacturersFilter,
      assigneesFilter,
      orderDateFilter,
      adminDateFilter,
      estimatedDeliveryDateFilter,
      receivedDateFilter,
      showInactiveFilter,
      drugsFilter,
      drugDoseFormCategoriesFilter,
      orderTypesFilter,
      billVoidedFilter,
      billVoidedDateFilter,
      orderViewFilter
    } = filters;
    let selectedFiltersNum = 0;

    if (statusesFilter?.length !== 0) selectedFiltersNum++;
    if (physiciansFilter?.length !== 0) selectedFiltersNum++;
    if (facilitiesFilter?.length !== 0) selectedFiltersNum++;
    if (manufacturersFilter?.length !== 0) selectedFiltersNum++;
    if (assigneesFilter?.length !== 0) selectedFiltersNum++;
    if (orderDateFilter?.fromDate !== ALL) selectedFiltersNum++;
    if (adminDateFilter?.fromDate !== ALL) selectedFiltersNum++;
    if (estimatedDeliveryDateFilter?.fromDate !== ALL) selectedFiltersNum++;
    if (receivedDateFilter?.fromDate !== ALL) selectedFiltersNum++;
    if (showInactiveFilter !== defaultFilters.showInactiveFilter) selectedFiltersNum++;
    if (orderTypesFilter?.length !== 0) selectedFiltersNum++;
    if (billVoidedFilter?.length !== 0) selectedFiltersNum++;
    if (billVoidedDateFilter?.length !== 0 && billVoidedDateFilter.fromDate !== ALL) selectedFiltersNum++;
    if (drugsFilter?.length !== 0) selectedFiltersNum++;
    if (drugDoseFormCategoriesFilter?.length !== 0) selectedFiltersNum++;
    if (orderViewFilter?.length !== 0 && orderViewFilter !== ORDER_VIEW.CURRENT_ORDER.value) selectedFiltersNum++;
    return selectedFiltersNum;
  };

  const searchRxnormDrugs = async (searchTerm) => {
    const response = await searchRxnormDrugsAPI({ text: searchTerm });
    return response?.data?.rxnormDrugs || [];
  };

  const assigneesForFilter = Object.keys(assignees).map((key) => ({
    text: formatAssignee(assignees[key]),
    value: assignees[key].id
  }));

  const {
    statusesFilter,
    physiciansFilter,
    facilitiesFilter,
    manufacturersFilter,
    assigneesFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    showInactiveFilter,
    orderTypesFilter,
    billVoidedFilter,
    billVoidedDateFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter
  } = selectedFilters;
  return (
    <StyledDiv filtersVisible={filtersVisible}>
      <StyledSpace
        direction={filtersVisible ? "vertical" : "horizontal"}
        size={0}
        filtersVisible={filtersVisible}
        isSavedListView={isSavedListView}
        isCalendar={isCalendar}
      >
        <div
          style={{
            display: "flex",
            cursor: "pointer",
            padding: filtersVisible ? "0 5px 5px 10px" : undefined,
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center"
          }}
          onClick={() => setFiltersVisible(!filtersVisible)}
        >
          <div
            id="filter_by_btn"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              paddingLeft: "5px"
            }}
          >
            <FilterByIcon />
            {selectedFiltersNum() > 0 ? (
              <div
                id="filters_selected"
                style={{
                  background: RED_GRAD,
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  marginTop: "-13px",
                  marginLeft: "-2px"
                }}
              />
            ) : (
              <></>
            )}
            <Text
              style={{
                color: NEW_BLUE,
                paddingLeft: 6,
                paddingTop: 2,
                fontWeight: 600,
                textTransform: "uppercase"
              }}
            >
              {t("filter_by")}
            </Text>
          </div>
          {filtersVisible ? <CloseOutlined style={{ color: NEW_BLUE, paddingRight: "15px" }} /> : <></>}
        </div>
        <div
          onClick={(e) => onClickReset(e)}
          style={{
            display: !filtersVisible ? "none" : "flex",
            justifyContent: "flex-end",
            padding: "0px 20px 15px 10px",
            cursor: "pointer"
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "5px" }}>
            <ClearIcon />
            <Text strong level={5} style={{ color: NEW_BLUE, paddingLeft: 4 }}>
              {t("clear_all")}
            </Text>
          </div>
        </div>
        <div style={{ padding: filtersVisible ? "0px 10px" : "0px 5px" }} bordered={false}>
          <Space
            size={16}
            direction="vertical"
            style={{ display: !filtersVisible ? "none" : undefined, padding: "0px 10px 10px 10px", width: "100%" }}
          >
            <Checkbox
              data-tm-id="show-active-orders-filter"
              id="how-active-orders-filter"
              checked={showInactiveFilter === "true"}
              onChange={(e) => handleShowInactiveCheckbox(e, "starredPatients")}
              style={{ width: "100%", paddingRight: "0px" }}
            >
              {t("show_inactive_orders")}
            </Checkbox>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: GRAY }}>
                {t("order_view")}
              </Text>
              <SingleSelect
                dataTMId="order-view-filter"
                changed={orderViewFilter !== defaultFilters.orderViewFilter}
                value={orderViewFilter}
                placeholder={t("all")}
                showSearch={false}
                onClear={() => {
                  handleInputSingleSelect(ORDER_VIEW.CURRENT_ORDER.value, "orderViewFilter");
                }}
                onSelect={(values) => {
                  handleInputSingleSelect(values, "orderViewFilter");
                }}
              >
                {Object.values(ORDER_VIEW).map((s, index) => {
                  const id = `order-view-filter_${index}`;
                  return (
                    <Option id={id} key={index} value={s.value}>
                      {s.title}
                    </Option>
                  );
                })}
              </SingleSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.order_type.title}
              </Text>
              <MultiSelect
                dataTMId="order-type-filter"
                id="order-type-filter"
                changed={orderTypesFilter !== defaultFilters.orderTypesFilter}
                value={orderTypesFilter}
                tooltipContent={multiples.orderTypesFilter || []}
                onChange={(value, options) => handleInputMultiSelect(value, "orderTypesFilter", options)}
                clearAll={true}
                onClearAll={() => handleInputMultiSelect([], "orderTypesFilter", [])}
                placeholder={t("all")}
                maxTagCount={1}
                maxTagTextLength={orderTypesFilter?.length > 1 ? 8 : 16}
              >
                {Object.values(ORDER_TYPES).map((o, index) => {
                  const id = `order-type-filter_${index}`;
                  return (
                    <Option id={id} key={index} value={o.value}>
                      {o.title}
                    </Option>
                  );
                })}
              </MultiSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.status.title}
              </Text>
              <MultiSelect
                dataTMId="cycle-status-filter"
                id="cycle-status-filter"
                changed={statusesFilter !== defaultFilters.statusesFilter}
                value={statusesFilter}
                tooltipContent={multiples.statusesFilter || []}
                onChange={(value, options) => handleInputMultiSelect(value, "statusesFilter", options)}
                clearAll={true}
                selectAll={true}
                onClearAll={() => handleInputMultiSelect([], "statusesFilter", [])}
                onSelectAll={() => {
                  const values = Object.keys(Statuses).filter((k) => !k.includes("-free"));
                  const statusesList = Object.entries(Statuses).map(([key, value]) => ({
                    value: key,
                    text: value.title(t)
                  }));

                  const options = statusesList
                    .filter((s) => !s.value.includes("-free"))
                    .map(({ value, text }, index) => ({
                      value: value,
                      key: `${index}`,
                      children: text
                    }));
                  handleInputMultiSelect(values, "statusesFilter", options);
                }}
                placeholder={t("all")}
                maxTagCount={1}
                maxTagTextLength={statusesFilter.length > 1 ? 8 : 16}
              >
                {Object.values(Statuses)
                  .filter((s) => !s.key.includes("-free"))
                  .map((s, index) => {
                    const id = `cycle-status-filter_${index}`;
                    return (
                      <Option id={id} key={index} value={s.key}>
                        {s.title(t)}
                      </Option>
                    );
                  })}
              </MultiSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.assigneeNames.title}
              </Text>
              <MultiSelect
                dataTMId={FILTER_PATIENT_ASSIGNEE}
                id={FILTER_PATIENT_ASSIGNEE}
                changed={assigneesFilter !== defaultFilters.assigneesFilter}
                value={assigneesFilter}
                onChange={(value, options) => handleInputMultiSelect(value, "assigneesFilter", options)}
                tooltipContent={multiples.assigneesFilter || []}
                placeholder={filters.isUnassigned ? t("unassigned") : `${t("all")}`}
                disabled={filters.isUnassigned}
                maxTagCount={1}
                maxTagTextLength={assigneesFilter.length > 1 ? 8 : 16}
                filterSort={filterSort}
                clearAll={true}
                selectAll={true}
                onClearAll={() => handleInputMultiSelect([], "assigneesFilter", [])}
                onSelectAll={() => {
                  const values = assigneesForFilter.map(({ value }) => value);
                  const options = assigneesForFilter.map(({ value, text }, index) => ({
                    value: value,
                    key: index,
                    children: text
                  }));

                  handleInputMultiSelect(values, "assigneesFilter", options);
                }}
              >
                <Option>{t("filter.assignee.no_assignee")} </Option>
                {_.uniqBy(assigneesForFilter, "value").map((assignee, index) => (
                  <Option key={index} value={assignee.value} localSortKey={assignee.text}>
                    {assignee.text}
                  </Option>
                ))}
              </MultiSelect>
              <Space id="patient_filter_unassignee">
                <Checkbox
                  style={{ paddingTop: 4 }}
                  checked={filters.isUnassigned}
                  onChange={(e) => handleCheckUnassigned(e.target.checked)}
                >
                  <Text style={{ color: GRAY }}>{t("unassigned")}</Text>
                </Checkbox>
              </Space>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.facility.title}
              </Text>
              <MultiSelect
                dataTMId="facility-filter"
                id="facility-filter"
                changed={facilitiesFilter !== defaultFilters.facility}
                value={facilitiesFilter}
                onChange={(value, options) => handleInputMultiSelect(value, "facilitiesFilter", options)}
                placeholder={t("all")}
                tooltipContent={facilityTooltipContent}
                showSearch={true}
                filterOption={false}
                onSearch={handleFacilitySearch}
                maxTagCount={1}
                maxTagTextLength={facilitiesFilter.length > 1 ? 8 : 16}
                clearAll={true}
                onClearAll={() => handleInputMultiSelect([], "facilitiesFilter", [])}
                notFoundContent={getFacilityNotFoundContent()}
              >
                {facilityOptions.map((fac) => (
                  <Option key={String(fac.id)} value={fac.id} label={fac.name} localSortKey={fac.name}>
                    {fac.name}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: GRAY }}>
                {t("patient.general.drug_name")}
              </Text>
              <Tooltip
                open={drugNameTextToDisplay && !isDrugNameSelectOpen && isDrugNameSelectHover}
                title={<Text>{drugNameTextToDisplay}</Text>}
                color={WHITE}
                placement="right"
                overlayStyle={{
                  maxWidth: "calc(100% - 600px)",
                  whiteSpace: "normal",
                  wordWrap: "break-word"
                }}
              >
                <MultiSelectDrugSearch
                  id="drug-filter-name"
                  dataTMId="drug-filter-name"
                  searchRxnormDrugs={searchRxnormDrugs}
                  mode="multiple"
                  placeholder={t("all")}
                  onChangeSelectedDrugs={(selectedDrugs) => handleSelectedDrugsChange(selectedDrugs)}
                  value={drugsFilter}
                  tooltipContent={multiples.drugsFilter || []}
                  bordered={false}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  displayNameMaxChars={25}
                  style={{
                    width: "100%",
                    border: `0px`,
                    borderBottom: `1px solid ${GRAY}`,
                    padding: 0,
                    color: drugsFilter !== defaultFilters.drugsFilter ? `${STEEL_BLUE}` : `${SEC_FONT_GREY}`
                  }}
                  onDropdownVisibleChange={(open) => setIsDrugNameSelectOpen(open)}
                  onMouseEnter={() => setIsDrugNameSelectHover(true)}
                  onMouseLeave={() => setIsDrugNameSelectHover(false)}
                  changed={drugsFilter !== defaultFilters.drugsFilter}
                  maxTagCount={1}
                  maxTagTextLength={drugsFilter.length == 1 ? 15 : 6}
                />
              </Tooltip>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text id="drug_dose_form_category_title" strong style={{ color: GRAY }}>
                {t("patient.general.dose_form_category")}
              </Text>

              <MultiSelect
                id="drug_dose_form_category_box"
                dataTMId="drug_dose_form_category_box"
                onChange={(value, options) => handleInputMultiSelect(value, "drugDoseFormCategoriesFilter", options)}
                value={drugDoseFormCategoriesFilter}
                tooltipContent={multiples.drugDoseFormCategoriesFilter || []}
                mode="multiple"
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
                changed={drugDoseFormCategoriesFilter !== defaultFilters.drugDoseFormCategoriesFilter}
                maxTagCount={2}
              >
                {DOSE_FORM_CATEGORY_TYPES.map((category) => {
                  const id = `drug_filter_dose_form_category_${category.value}`;
                  return (
                    <Option id={id} key={category.value} value={category.value}>
                      {t(category.text)}
                    </Option>
                  );
                })}
              </MultiSelect>
            </Space>

            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.drug_manufacturer.title}
              </Text>
              <MultiSelect
                dataTMId="manufacturer-filter"
                id="manufacturer-filter"
                changed={manufacturersFilter !== defaultFilters.manufacturer}
                value={manufacturersFilter}
                tooltipContent={sponsorTooltipContent}
                onChange={(value, options) => handleInputMultiSelect(value, "manufacturersFilter", options)}
                placeholder={t("all")}
                showSearch={true}
                clearAll={true}
                maxTagCount={1}
                maxTagTextLength={manufacturersFilter.length == 1 ? 15 : 6}
                onClearAll={() => handleInputMultiSelect([], "manufacturersFilter", [])}
                onSearch={handleSponsorSearch}
                filterOption={false}
                notFoundContent={getSponsorNotFoundContent()}
              >
                {sponsorSearchResults.map((sponsor, index) => (
                  <Option key={index} value={sponsor}>
                    {sponsor}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("order_date")}
              </Text>
              <DateRangeFilter
                dataTMId="order_date_filter"
                id="order_date_filter"
                timeRange={orderDateFilter}
                onSelect={(fromDate, toDate) => handleInputDate(fromDate, toDate, "orderDateFilter")}
                showSearch={false}
                onClear={() => {
                  handleInputDate(THIS_MONTH, null, "orderDateFilter");
                }}
              />
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("admin_date")}
              </Text>
              <DateRangeFilter
                id="admin_date_filter"
                dataTMId="admin_date_filter"
                timeRange={adminDateFilter}
                onSelect={(fromDate, toDate) => handleInputDate(fromDate, toDate, "adminDateFilter")}
                showSearch={false}
                onClear={() => {
                  handleInputDate(ALL, null, "adminDateFilter");
                }}
              />
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("estimated_delivery_date")}
              </Text>
              <DateRangeFilter
                id="estimated_delivery_date_filter"
                dataTMId="estimated_delivery_date_filter"
                timeRange={estimatedDeliveryDateFilter}
                options={[ALL, THIS_WEEK, THIS_MONTH]}
                onSelect={(fromDate, toDate) => handleInputDate(fromDate, toDate, "estimatedDeliveryDateFilter")}
                showSearch={false}
                defaultValue={ALL}
                placeholder={t("all_times")}
                onClear={() => {
                  handleInputDate(ALL, null, "estimatedDeliveryDateFilter");
                }}
              />
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("received_date")}
              </Text>
              <DateRangeFilter
                id="received_date_filter"
                dataTMId="received_date_filter"
                timeRange={receivedDateFilter}
                options={[ALL, LAST_WEEK, LAST_MONTH]}
                onSelect={(fromDate, toDate) => handleInputDate(fromDate, toDate, "receivedDateFilter")}
                showSearch={false}
                defaultValue={ALL}
                placeholder={t("all_times")}
                onClear={() => {
                  handleInputDate(ALL, null, "receivedDateFilter");
                }}
              />
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("bill_voided")}
              </Text>
              <MultiSelect
                dataTMId="bill-voided-filter"
                changed={billVoidedFilter !== defaultFilters.billVoidedFilter}
                value={billVoidedFilter}
                tooltipContent={multiples.billVoidedFilter || []}
                onChange={(value, options) => handleInputMultiSelect(value, "billVoidedFilter", options)}
                placeholder={t("all")}
                maxTagCount={2}
              >
                {Object.values(BILL_VOIDED).map((s, index) => {
                  const id = `bill-voided-filter_${index}`;
                  return (
                    <Option id={id} key={index} value={s.value}>
                      {s.title}
                    </Option>
                  );
                })}
              </MultiSelect>
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {t("bill_voided_date")}
              </Text>
              <DateRangeFilter
                dataTMId="bill-voided-date-filter"
                id="bill-voided-date-filter"
                timeRange={billVoidedDateFilter}
                options={[ALL, LAST_WEEK, LAST_MONTH]}
                onSelect={(fromDate, toDate) => handleInputDate(fromDate, toDate, "billVoidedDateFilter")}
                showSearch={false}
                onClear={() => {
                  handleInputDate(ALL, null, "billVoidedDateFilter");
                }}
              />
            </Space>
            <Space size={0} direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#64666C" }}>
                {patientTableColumns.physician.title}
              </Text>
              <MultiSelect
                dataTMId="physician-filter"
                id="physician-filter"
                changed={physiciansFilter !== defaultFilters.physician}
                value={selectedNpis}
                tooltipContent={physicianTooltipContent}
                onChange={handlePhysicianChange}
                placeholder={t("all")}
                filterOption={false}
                maxTagCount={1}
                maxTagTextLength={selectedNpis.length == 1 ? 15 : 5}
                clearAll={true}
                searchValue={physicianSearchText}
                onSearch={handlePhysicianSearch}
                notFoundContent={getPhysicianNotFoundContent()}
                onDropdownVisibleChange={(open) => {
                  if (!open) {
                    setPhysicianSearchText("");
                    setPhysicianSearchResults([]);
                  }
                }}
                onSelect={() => setPhysicianSearchText("")}
                onClearAll={() => handlePhysicianChange([], [])}
              >
                {allPhysicianOptions.map(({ npi, name }) => (
                  <Option key={npi} value={npi} id={`physician-filter_${npi}`} label={name} localSortKey={name}>
                    {name}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          </Space>
        </div>
      </StyledSpace>
    </StyledDiv>
  );
};

const mapStateToProps = createStructuredSelector({
  patients: selectPatients,
  filters: selectFilters,
  assignees: selectAssigneesAndUserGroups
});

const OrderFilters = connect(mapStateToProps)(withTranslation()(OrderFiltersComponent));

const applyFilters = (patients, filters, clearAll = false) => {
  const {
    statusesFilter,
    physiciansFilter,
    facilitiesFilter,
    manufacturersFilter,
    showInactiveFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugDoseFormCategoriesFilter
  } = filters;
  const showHistoricOrders = showInactiveFilter === "true";

  const takePhysicianId = (physician) => {
    return _.get(physician, "id", physician);
  };

  const filteredPaitents = patients.filter(
    (patient) =>
      (clearAll ||
        statusesFilter.length === 0 ||
        statusesFilter.includes(patient.currentOrder.status.split("-free")[0])) &&
      (clearAll ||
        physiciansFilter.length === 0 ||
        physiciansFilter.includes(String(takePhysicianId(patient.physician)))) &&
      (clearAll || facilitiesFilter.length === 0 || facilitiesFilter.includes(patient.facility)) &&
      (clearAll ||
        manufacturersFilter.length === 0 ||
        (patient.potentialSaving && manufacturersFilter.includes(patient.potentialSaving.providedBy))) &&
      ((clearAll && patient.isActive) ||
        (!clearAll && (showHistoricOrders || showHistoricOrders !== patient.isActive))) &&
      (clearAll ||
        orderTypesFilter?.length === 0 ||
        orderTypesFilter
          .map((filter) => (filter === "replacement" ? 0 : 1)) // Convert back to isFreeDrug value so the matching works correctly
          .includes(patient.currentOrder.isFreeDrug)) &&
      (clearAll || billVoidedFilter.length === 0 || billVoidedFilter.includes(patient.currentOrder?.billVoided)) &&
      (clearAll ||
        drugDoseFormCategoriesFilter.length === 0 ||
        drugDoseFormCategoriesFilter.some((category) => patient.rxnormDrug[category] === 1) ||
        !patient.rxnormDrug.doseFormCategory)
  );
  return filteredPaitents;
};

export { OrderFilters, applyFilters };
